import { useMemo, useState } from 'react';
import { AgentConfigValues } from '../../types/database';
import { AGENT_CONFIG_SCHEMA, ALL_KNOBS, Knob, path } from '../../config/agentConfigSchema';

// Editor for a single scope's RealityAgent config override (global / room / PC).
// Produces a PARTIAL config object keyed by yaml section then key — only knobs
// left set to a concrete value are stored; everything else inherits the next
// scope up (PC → room → global → the on-disk config.yaml). Restart-to-apply:
// the agent reads these at boot, so a saved change needs an agent restart.

const boolToStr = (v: unknown): string => (v === true ? 'on' : v === false ? 'off' : '');

// Read a stored override object into flat form state keyed by "section.key".
function configToForm(config: AgentConfigValues | undefined): Record<string, string> {
  const form: Record<string, string> = {};
  if (!config) return form;
  for (const k of ALL_KNOBS) {
    const val = config[k.section]?.[k.key];
    if (val === undefined || val === null) continue;
    form[path(k)] = k.type === 'bool' ? boolToStr(val) : String(val);
  }
  return form;
}

// Build the partial override object from form state (only set knobs).
function formToConfig(form: Record<string, string>): AgentConfigValues {
  const config: AgentConfigValues = {};
  for (const k of ALL_KNOBS) {
    const s = form[path(k)];
    if (s === undefined || s === '') continue; // inherit
    let val: any;
    if (k.type === 'bool') val = s === 'on';
    else if (k.type === 'int') {
      const n = Number(s);
      if (!Number.isFinite(n)) continue;
      val = Math.trunc(n);
    } else val = s;
    (config[k.section] ??= {})[k.key] = val;
  }
  return config;
}

// One-line summary of a stored override object, for the scope rows.
export function summarizeAgentConfig(config: AgentConfigValues | undefined): string {
  if (!config) return '';
  const keys: string[] = [];
  for (const k of ALL_KNOBS) {
    if (config[k.section]?.[k.key] !== undefined) keys.push(path(k));
  }
  if (keys.length === 0) return '';
  const shown = keys.slice(0, 3).join(', ');
  return keys.length > 3 ? `${keys.length} overrides: ${shown}…` : `${keys.length} override${keys.length > 1 ? 's' : ''}: ${shown}`;
}

// Count of set overrides in a stored object.
export function countAgentConfig(config: AgentConfigValues | undefined): number {
  if (!config) return 0;
  return ALL_KNOBS.reduce((n, k) => (config[k.section]?.[k.key] !== undefined ? n + 1 : n), 0);
}

export default function AgentConfigForm({
  title,
  initial,
  inherited,
  onSave,
  onClose,
}: {
  title: string;
  initial: AgentConfigValues | undefined;
  // Effective values from the scopes above this one (for the "inherit (X)" hint).
  inherited?: AgentConfigValues;
  // null = clear the whole row (back to fully inherited).
  onSave: (config: AgentConfigValues | null) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState<Record<string, string>>(() => configToForm(initial));
  const [saving, setSaving] = useState(false);
  const config = useMemo(() => formToConfig(form), [form]);
  const hadConfig = countAgentConfig(initial) > 0;

  const set = (p: string, v: string) => setForm((f) => ({ ...f, [p]: v }));

  const save = async (value: AgentConfigValues | null) => {
    setSaving(true);
    try {
      await onSave(value);
    } finally {
      setSaving(false);
    }
  };

  // Inherited value hint text for a knob ("inherit" or "inherit (currently X)").
  const inheritHint = (k: Knob): string => {
    const v = inherited?.[k.section]?.[k.key];
    if (v === undefined || v === null) return 'inherit';
    return `inherit (${k.type === 'bool' ? (v ? 'on' : 'off') : v})`;
  };

  const renderControl = (k: Knob) => {
    const p = path(k);
    const val = form[p] ?? '';
    if (k.type === 'bool') {
      return (
        <select value={val} onChange={(e) => set(p, e.target.value)}>
          <option value="">{inheritHint(k)}</option>
          <option value="on">On</option>
          <option value="off">Off</option>
        </select>
      );
    }
    if (k.type === 'string' && k.options) {
      return (
        <select value={val} onChange={(e) => set(p, e.target.value)}>
          <option value="">{inheritHint(k)}</option>
          {k.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }
    // int (and free string, if ever added)
    return (
      <input
        type={k.type === 'int' ? 'number' : 'text'}
        placeholder={inheritHint(k)}
        value={val}
        onChange={(e) => set(p, e.target.value)}
      />
    );
  };

  return (
    <div className="agent-config-editor">
      <div className="agent-config-editor-title">{title}</div>
      <p className="agent-config-editor-hint">
        Blank = inherit from the scope above. Set a value to override it here. Changes apply after the
        agent restarts.
      </p>

      <div className="agent-config-sections">
        {AGENT_CONFIG_SCHEMA.map((sec) => (
          <fieldset key={sec.section} className="agent-config-group">
            <legend>{sec.title}</legend>
            {sec.knobs.map((k) => (
              <label key={path(k)} className={`agent-config-field${k.danger ? ' danger' : ''}`}>
                <span className="agent-config-field-label">
                  {k.label}
                  {k.danger && <span className="agent-config-danger-tag"> ⚠</span>}
                  <span className="agent-config-field-desc">{k.description}</span>
                </span>
                {renderControl(k)}
              </label>
            ))}
          </fieldset>
        ))}
      </div>

      {countAgentConfig(config) > 0 ? (
        <pre className="agent-config-preview">{JSON.stringify(config, null, 2)}</pre>
      ) : (
        <p className="agent-config-preview-empty">Nothing overridden — saving clears this scope’s row.</p>
      )}

      <div className="software-actions">
        <button disabled={saving} onClick={() => save(countAgentConfig(config) > 0 ? config : null)}>
          Save
        </button>
        {hadConfig && (
          <button disabled={saving} onClick={() => save(null)}>
            Clear (inherit all)
          </button>
        )}
        <button disabled={saving} onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}
